// Picker — the combined emoji + GIF window (Discord-style tabs). One panel with an "Emoji" tab
// (full categorized, searchable set) and a "GIFs" tab (server-proxied Giphy). Every button is
// type="button" because this renders inside the composer <form> (a default submit would fire a send).
import { useEffect, useRef, useState } from "react";
import { GifsApi } from "../hooks/useApi.js";
import Icon from "./Icon.jsx";
import { EMOJI_CATEGORIES, searchEmoji } from "../emoji.js";

function EmojiTab({ onPick }) {
  const [q, setQ] = useState("");
  const scrollRef = useRef(null);
  const results = q.trim() ? searchEmoji(q) : null;
  const jump = (id) => scrollRef.current?.querySelector(`[data-cat="${id}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  return (
    <div className="picker__body">
      <div className="picker__search">
        <Icon name="search" className="ic--sm" />
        <input autoFocus value={q} placeholder="Find the perfect emoji" onChange={(e) => setQ(e.target.value)} />
      </div>
      {!results && (
        <div className="picker__cats">
          {EMOJI_CATEGORIES.map((c) => (
            <button key={c.id} type="button" className="picker__cat" title={c.label}
                    onMouseDown={(e) => { e.preventDefault(); jump(c.id); }}>{c.icon}</button>
          ))}
        </div>
      )}
      <div className="picker__scroll" ref={scrollRef}>
        {results ? (
          results.length
            ? <div className="picker__grid">{results.map((e, i) => (
                <button key={i} type="button" className="picker__emoji" onMouseDown={(ev) => { ev.preventDefault(); onPick(e); }}>{e}</button>
              ))}</div>
            : <div className="picker__empty">No emoji found</div>
        ) : EMOJI_CATEGORIES.map((c) => (
          <div key={c.id} data-cat={c.id} className="picker__section">
            <div className="picker__label">{c.label}</div>
            <div className="picker__grid">
              {c.emojis.map(([ch], i) => (
                <button key={i} type="button" className="picker__emoji" title={ch}
                        onMouseDown={(ev) => { ev.preventDefault(); onPick(ch); }}>{ch}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function GifTab({ onPick }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const reqRef = useRef(0);

  useEffect(() => {
    const id = ++reqRef.current;
    setLoading(true);
    const query = q.trim();
    const t = setTimeout(async () => {
      try {
        const res = query ? await GifsApi.search(query, 0) : await GifsApi.trending(0);
        if (reqRef.current !== id) return;
        setItems(res.items || []); setHasMore(!!res.hasMore); setPage(0);
      } catch { if (reqRef.current === id) { setItems([]); setHasMore(false); } }
      finally { if (reqRef.current === id) setLoading(false); }
    }, query ? 350 : 0);
    return () => clearTimeout(t);
  }, [q]);

  async function loadMore() {
    if (loading || !hasMore) return;
    const next = page + 1;
    const query = q.trim();
    setLoading(true);
    try {
      const res = query ? await GifsApi.search(query, next) : await GifsApi.trending(next);
      setItems((prev) => [...prev, ...(res.items || [])]);
      setHasMore(!!res.hasMore); setPage(next);
    } catch { /* keep what we have */ }
    finally { setLoading(false); }
  }
  const onScroll = (e) => { const el = e.target; if (el.scrollHeight - el.scrollTop - el.clientHeight < 260) loadMore(); };

  return (
    <div className="picker__body">
      <div className="picker__search">
        <Icon name="search" className="ic--sm" />
        <input autoFocus value={q} placeholder="Search GIFs" onChange={(e) => setQ(e.target.value)} />
      </div>
      <div className="picker__scroll picker__gifgrid" onScroll={onScroll}>
        {items.map((g) => (
          <button key={g.id} type="button" className="picker__gif" onMouseDown={(e) => { e.preventDefault(); onPick(g); }}>
            <img src={g.preview} alt="" loading="lazy" />
          </button>
        ))}
        {loading && <div className="picker__loading"><span className="btn__spin" /></div>}
        {!loading && items.length === 0 && <div className="picker__empty">No GIFs found</div>}
      </div>
      <div className="picker__attr">Powered by GIPHY</div>
    </div>
  );
}

export default function Picker({ open, tab, onTab, onEmoji, onGif, onClose }) {
  if (!open) return null;
  const active = tab === "gif" ? "gif" : "emoji";
  return (
    <div className="picker">
      <div className="picker__tabs">
        <button type="button" className={`picker__tab ${active === "gif" ? "is-on" : ""}`} onClick={() => onTab("gif")}>GIFs</button>
        <button type="button" className={`picker__tab ${active === "emoji" ? "is-on" : ""}`} onClick={() => onTab("emoji")}>Emoji</button>
        <button type="button" className="picker__close" title="Close" onClick={onClose}><Icon name="close" className="ic--sm" /></button>
      </div>
      {active === "gif" ? <GifTab onPick={onGif} /> : <EmojiTab onPick={onEmoji} />}
    </div>
  );
}
